/**
 * Implémentation HTTP de `AccompApi` — appelle le vrai backend boostcore
 * (`/accompagnement`) via `apiFetch` (CSRF + credentials + retry 401 gérés).
 *
 * Le serveur dérive l'acteur de la session (`req.user`) → le paramètre `actor`
 * du contrat est ignoré ici (placeholders `_actor`). Le store traduit déjà la
 * `view` en filtres ; on mappe juste `page/pageSize → limit/offset` et les
 * tableaux en CSV. Les réponses sont déjà au format `AccompRequest`.
 */
import { apiFetch } from '@/auth/apiFetch'
import type { AccompApi } from './accompagnementApi'
import type {
  AccompActionInput,
  AccompActor,
  AccompCreateInput,
  AccompListFilters,
  AccompListResponse,
  AccompOutcome,
  AccompQuotePick,
  AccompRequest,
  AccompStats,
  AccompStatsPeriod,
  AccompTechnician,
  AccompUpdateInput,
  AccompUserPick,
} from './accompagnementTypes'

const BASE = '/accompagnement'
const JSON_HEADERS = { 'Content-Type': 'application/json' }

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

function qs(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, v)
  }
  const s = usp.toString()
  return s ? `?${s}` : ''
}

function post(path: string, body: unknown): Promise<AccompRequest> {
  return apiFetch(path, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }).then(
    json<AccompRequest>,
  )
}

export const accompagnementHttpApi: AccompApi = {
  list(f: AccompListFilters): Promise<AccompListResponse> {
    const query = qs({
      q: f.q,
      status: f.status?.length ? f.status.join(',') : undefined,
      urgency: f.urgency?.length ? f.urgency.join(',') : undefined,
      assignedToUuid: f.assignedToUuid,
      unassignedOnly: f.unassignedOnly ? 'true' : undefined,
      createdByUuids: f.createdByUuids?.length ? f.createdByUuids.join(',') : undefined,
      limit: String(f.pageSize),
      offset: String((f.page - 1) * f.pageSize),
      sort: f.sort,
      order: f.order,
    })
    return apiFetch(`${BASE}${query}`, { signal: f.signal }).then(json<AccompListResponse>)
  },

  get(id: string): Promise<AccompRequest> {
    return apiFetch(`${BASE}/${id}`).then(json<AccompRequest>)
  },

  listByClient(clientUuid: string): Promise<AccompRequest[]> {
    return apiFetch(`${BASE}/client/${clientUuid}`).then(json<AccompRequest[]>)
  },

  create(input: AccompCreateInput, _actor: AccompActor): Promise<AccompRequest> {
    return post(BASE, input)
  },

  update(id: string, patch: AccompUpdateInput, _actor: AccompActor): Promise<AccompRequest> {
    return apiFetch(`${BASE}/${id}`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(patch),
    }).then(json<AccompRequest>)
  },

  assign(id: string, technicianUuid: string, _actor: AccompActor, note?: string): Promise<AccompRequest> {
    return post(`${BASE}/${id}/assign`, { technicianUuid, note })
  },

  takeCharge(id: string, _actor: AccompActor): Promise<AccompRequest> {
    return post(`${BASE}/${id}/take-charge`, {})
  },

  refuse(id: string, note: string, _actor: AccompActor): Promise<AccompRequest> {
    return post(`${BASE}/${id}/refuse`, { note })
  },

  remind(id: string, note: string, _actor: AccompActor): Promise<AccompRequest> {
    return post(`${BASE}/${id}/remind`, { note })
  },

  addComment(id: string, message: string, _actor: AccompActor): Promise<AccompRequest> {
    return post(`${BASE}/${id}/comment`, { message })
  },

  addAction(id: string, input: AccompActionInput, _actor: AccompActor): Promise<AccompRequest> {
    return post(`${BASE}/${id}/actions`, input)
  },

  deleteAction(id: string, actionId: string): Promise<AccompRequest> {
    return apiFetch(`${BASE}/${id}/actions/${actionId}`, { method: 'DELETE' }).then(json<AccompRequest>)
  },

  listQuotes(id: string, from?: string, to?: string): Promise<AccompQuotePick[]> {
    return apiFetch(`${BASE}/${id}/quotes${qs({ from, to })}`).then(json<AccompQuotePick[]>)
  },

  attachQuote(id: string, quoteUuid: string): Promise<AccompRequest> {
    return post(`${BASE}/${id}/quotes`, { quoteUuid })
  },

  detachQuote(id: string, linkId: string): Promise<AccompRequest> {
    return apiFetch(`${BASE}/${id}/quotes/${linkId}`, { method: 'DELETE' }).then(json<AccompRequest>)
  },

  close(id: string, outcome: AccompOutcome, _actor: AccompActor, note?: string): Promise<AccompRequest> {
    return post(`${BASE}/${id}/close`, { outcome, note })
  },

  cancel(id: string, _actor: AccompActor, note?: string): Promise<AccompRequest> {
    return post(`${BASE}/${id}/cancel`, { note })
  },

  listTechnicians(): Promise<AccompTechnician[]> {
    return apiFetch(`${BASE}/technicians`).then(json<AccompTechnician[]>)
  },

  searchUsers(q: string, signal?: AbortSignal): Promise<AccompUserPick[]> {
    return apiFetch(`${BASE}/users${qs({ q })}`, { signal }).then(json<AccompUserPick[]>)
  },

  stats(period: AccompStatsPeriod): Promise<AccompStats> {
    return apiFetch(`${BASE}/stats${qs({ from: period.from, to: period.to })}`).then(json<AccompStats>)
  },
}
